import type { PartLine, TimelineNote, VoicePart } from '@corocanto/scoring';
import { onLine, VOICE_PARTS } from '@corocanto/scoring';

/**
 * A playback channel: one per voice part, one "Eşlik" channel for all accompaniment staves, and
 * "other": where the chorist's own part divides, the line they do not sing ("2. Soprano" for a
 * first soprano). The presets treat it like another part.
 */
export type Channel = VoicePart | 'acc' | 'other';
export const CHANNELS: readonly Channel[] = ['S', 'A', 'T', 'B', 'acc', 'other'];
/** The voice channels the presets set: the four parts and the other line of a divided part. */
type VoiceChannel = VoicePart | 'other';
const VOICE_CHANNELS: readonly VoiceChannel[] = [...VOICE_PARTS, 'other'];

/** The part and line the chorist sings, which decides what "my part" is. */
export interface MyLine {
  part: VoicePart;
  line: PartLine;
}

/**
 * "What to play" presets. They are shortcuts that set the mixer; the mixer alone decides what
 * is heard, so what the faders show is what plays.
 */
export type PlaybackMode = 'mine' | 'all' | 'mineLouder' | 'allButMine' | 'accompaniment';
export const MODES: readonly PlaybackMode[] = ['mine', 'all', 'mineLouder', 'allButMine', 'accompaniment'];

/** The default level of every channel ("Tüm partiler" resets the voices to it). */
export const DEFAULT_LEVEL = 0.8;
/** "All, mine louder": my part at the top, the other voices this low (about −9 dB below the default). */
export const MINE_LOUDER_LEVEL = 1;
export const OTHERS_WHEN_MINE_LOUDER = 0.3;

/** One mixer channel: its level, mute, and solo (with any channel on solo, only the soloed ones play). */
export interface ChannelSetting {
  volume: number;
  muted: boolean;
  solo: boolean;
}
export type Mixer = Record<Channel, ChannelSetting>;

/** Every channel at the default level, nothing muted or soloed. Changes are kept only while a page is open. */
export const DEFAULT_MIXER: Mixer = Object.fromEntries(CHANNELS.map((ch) => [ch, { volume: DEFAULT_LEVEL, muted: false, solo: false }])) as Mixer;

/** The channel that plays a note: the other line of my divided part goes to "other". */
export function channelOf(note: Pick<TimelineNote, 'voicePart' | 'line'>, mine?: MyLine | null): Channel {
  if (mine && note.voicePart === mine.part && !onLine(note, mine.line)) return 'other';
  return note.voicePart ?? 'acc';
}

/** The presets that make sense: without a part of one's own, only "all" and "accompaniment only". */
export function modesFor(myPart: VoicePart | null): PlaybackMode[] {
  return myPart ? [...MODES] : ['all', 'accompaniment'];
}

/** Linear gain per channel: the fader level, or 0 when muted. With any channel on solo, only the soloed ones play. */
export function channelGains(mixer: Mixer): Record<Channel, number> {
  const anySolo = CHANNELS.some((ch) => mixer[ch].solo);
  const gains = {} as Record<Channel, number>;
  for (const ch of CHANNELS) gains[ch] = (anySolo ? mixer[ch].solo : !mixer[ch].muted) ? mixer[ch].volume : 0;
  return gains;
}

/** Solos are cleared by every preset. */
export function withoutSolo(mixer: Mixer): Mixer {
  return Object.fromEntries(CHANNELS.map((ch) => [ch, { ...mixer[ch], solo: false }])) as Mixer;
}

export function toggleMute(mixer: Mixer, ch: Channel): Mixer {
  return { ...mixer, [ch]: { ...mixer[ch], muted: !mixer[ch].muted } };
}

export function toggleSolo(mixer: Mixer, ch: Channel): Mixer {
  return { ...mixer, [ch]: { ...mixer[ch], solo: !mixer[ch].solo } };
}

const voice = (mixer: Mixer, part: VoiceChannel, muted: boolean, volume = mixer[part].volume): Mixer => ({
  ...mixer,
  [part]: { ...mixer[part], volume, muted },
});

/** Exactly the voice levels "all, mine louder" sets (so leaving it can undo them; levels set by hand are kept). */
function isStandardMineLouder(mixer: Mixer, myPart: VoicePart): boolean {
  return VOICE_CHANNELS.every((p) => mixer[p].volume === (p === myPart ? MINE_LOUDER_LEVEL : OTHERS_WHEN_MINE_LOUDER));
}

/**
 * Set the mixer for a preset.
 * - The voice presets never switch the accompaniment on or off, so switching it off stays off.
 * - "Only mine" and "all except mine" only mute and unmute voices; levels set by hand are kept.
 * - "All parts" unmutes every voice at the default level; "all, mine louder" unmutes every voice
 *   with mine at the top and the others low, and brings the accompaniment down to the others' level.
 *   Leaving "all, mine louder" first undoes its levels.
 * - "Accompaniment only" mutes every voice and turns the accompaniment on.
 */
export function applyPreset(preset: PlaybackMode, myPart: VoicePart | null, mixer: Mixer): Mixer {
  let m = withoutSolo(mixer);
  if (myPart && preset !== 'mineLouder' && isStandardMineLouder(m, myPart)) {
    for (const p of VOICE_CHANNELS) m = voice(m, p, m[p].muted, DEFAULT_LEVEL);
    if (m.acc.volume === OTHERS_WHEN_MINE_LOUDER) m = { ...m, acc: { ...m.acc, volume: DEFAULT_LEVEL } };
  }
  for (const p of VOICE_CHANNELS) {
    const mine = p === myPart;
    switch (preset) {
      case 'mine':
        m = voice(m, p, !mine);
        break;
      case 'allButMine':
        m = voice(m, p, mine);
        break;
      case 'all':
        m = voice(m, p, false, DEFAULT_LEVEL);
        break;
      case 'mineLouder':
        m = myPart ? voice(m, p, false, mine ? MINE_LOUDER_LEVEL : OTHERS_WHEN_MINE_LOUDER) : voice(m, p, false, DEFAULT_LEVEL);
        break;
      case 'accompaniment':
        m = voice(m, p, true);
        break;
    }
  }
  if (preset === 'accompaniment') m = { ...m, acc: { ...m.acc, muted: false } };
  if (preset === 'mineLouder' && myPart) m = { ...m, acc: { ...m.acc, volume: OTHERS_WHEN_MINE_LOUDER } };
  return m;
}

/**
 * The preset the mixer currently matches, or null ("Özel", custom). Only the song's own voice
 * parts count (with "other" when the chorist's part divides); the accompaniment does not, except
 * for "accompaniment only". A solo matches none.
 */
export function matchingPreset(mixer: Mixer, myPart: VoicePart | null, songParts: readonly VoiceChannel[], hasAccompaniment: boolean): PlaybackMode | null {
  if (CHANNELS.some((ch) => mixer[ch].solo)) return null;
  const parts = songParts.length > 0 ? songParts : VOICE_PARTS;
  const on = (p: VoiceChannel) => !mixer[p].muted && mixer[p].volume > 0;
  const others = parts.filter((p) => p !== myPart);
  const mineIn = myPart !== null && parts.includes(myPart);
  if (parts.every((p) => !on(p))) return hasAccompaniment && !mixer.acc.muted && mixer.acc.volume > 0 ? 'accompaniment' : null;
  if (mineIn && on(myPart) && others.every((p) => !on(p))) return 'mine';
  if (mineIn && !on(myPart) && others.every(on)) return 'allButMine';
  if (!parts.every(on)) return null;
  const level = (p: VoiceChannel) => mixer[p].volume;
  if (parts.every((p) => level(p) === level(parts[0] as VoiceChannel))) return 'all';
  if (mineIn && others.every((p) => level(myPart) > level(p) && level(p) === level(others[0] as VoiceChannel))) return 'mineLouder';
  return null;
}
